import { useEffect, useRef, useState } from "react"
import { useMapsLibrary } from "@vis.gl/react-google-maps"

export type UseAutocompleteSuggestionsReturn = {
  suggestions: google.maps.places.AutocompleteSuggestion[]
  isLoading: boolean
  resetSession: () => void
}

/**
 * A reusable hook that retrieves autocomplete suggestions from the Google Places API.
 * The data is loaded from the new Autocomplete Data API.
 * (https://developers.google.com/maps/documentation/javascript/place-autocomplete-data)
 *
 * @param inputString The input string for which to fetch autocomplete suggestions.
 * @param requestOptions Additional options for the autocomplete request
 *   (See {@link https://developers.google.com/maps/documentation/javascript/reference/autocomplete-data#AutocompleteRequest}).
 *
 * @returns An object containing the autocomplete suggestions, the current loading-status,
 *   and a function to reset the session.
 *
 * @example
 * ```jsx
 * const MyComponent = () => {
 *   const [input, setInput] = useState('');
 *   const { suggestions, isLoading, resetSession } = useAutocompleteSuggestions(input, {
 *     includedPrimaryTypes: ['restaurant']
 *   });
 *
 *   return (
 *     <div>
 *       <input value={input} onChange={(e) => setInput(e.target.value)} />
 *       <ul>
 *         {suggestions.map(({placePrediction}) => (
 *           <li key={placePrediction.placeId}>{placePrediction.text.text}</li>
 *         ))}
 *       </ul>
 *     </div>
 *   );
 * }
 * ```
 */
export function useAutocompleteSuggestions(
  inputString: string,
  requestOptions: Partial<google.maps.places.AutocompleteRequest> = {},
): UseAutocompleteSuggestionsReturn {
  const placesLib = useMapsLibrary("places")

  // stores the current sessionToken
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null)

  // the suggestions based on the specified input
  const [suggestions, setSuggestions] = useState<
    google.maps.places.AutocompleteSuggestion[]
  >([])

  // indicates if there is currently an incomplete request to the places API
  const [isLoading, setIsLoading] = useState(false)

  // once the PlacesLibrary is loaded and whenever the input changes, a query
  // is sent to the Autocomplete Data API.
  useEffect(() => {
    if (!placesLib) return

    const { AutocompleteSessionToken, AutocompleteSuggestion } = placesLib

    // Create a new session if one doesn't already exist. This has to be reset
    // after `fetchFields` for one of the returned places is called by calling
    // the `resetSession` function returned from this hook.
    if (!sessionTokenRef.current) {
      console.log("[DEBUG] Creating new token")
      sessionTokenRef.current = new AutocompleteSessionToken()
    }

    const bounds = new google.maps.LatLngBounds({
      south: 42.0, // Excludes Turkey, includes Greece and southern Italy
      west: -10.0, // Western Europe (Portugal, Ireland)
      north: 58.0, // Excludes Norway and Sweden, includes Denmark
      east: 30.0, // Excludes Turkey, includes most of Eastern Europe
    })

    // Non-EU European countries (Norway, Switzerland, Iceland, UK, Balkans, etc.)
    const includedRegionCodes = [
      "eu",
      "no",
      "ch",
      "is",
      "gb",
      "rs",
      "ba",
      "me",
      "mk",
      "al",
      "md",
      "ua",
      "by",
      "ru",
      "tr",
    ]

    const request: google.maps.places.AutocompleteRequest = {
      ...requestOptions,
      input: inputString,
      sessionToken: sessionTokenRef.current,
      locationBias: bounds,
      includedRegionCodes,
    }

    if (inputString === "") {
      if (suggestions.length > 0) setSuggestions([])
      return
    }

    setIsLoading(true)
    console.log("[DEBUG] - Fetching suggestions")
    AutocompleteSuggestion.fetchAutocompleteSuggestions(request).then((res) => {
      setSuggestions(res.suggestions)
      setIsLoading(false)
    })
  }, [placesLib, inputString])

  return {
    suggestions,
    isLoading,
    resetSession: () => {
      sessionTokenRef.current = null
      setSuggestions([])
    },
  }
}
